import { toast } from 'sonner';

/**
 * Mapping of frontend events → preconditions for triggering a status bump
 * prompt. Each event lists the appraisal_status values where a bump is
 * meaningful, plus the SUGGESTED next status (the backend
 * /pipeline/advance endpoint always advances to the canonical next in
 * STATUS_FLOW, so trigger_statuses match the position one step before
 * suggest_status).
 *
 * The user explicitly asked for a CHECK STAGE (confirmation toast) rather
 * than silent auto-advance — every event surfaces a toast with explicit
 * [Advance] / [Not yet] actions.
 */
export const STAGE_BUMP_EVENTS = {
  quotation_pdf_generated: {
    trigger_statuses: ['Quote Sent'],
    suggest_status: 'Quote Sent',
    label_prefix: 'Quotation PDF generated',
  },
  detailed_quotation_pdf_generated: {
    trigger_statuses: ['Quote Sent'],
    suggest_status: 'Quote Sent',
    label_prefix: 'Detailed Quotation generated',
  },
  invoice_pdf_generated: {
    trigger_statuses: ['Completed'],
    suggest_status: 'Invoiced',
    label_prefix: 'Invoice PDF generated',
  },
  invoice_created: {
    trigger_statuses: ['Completed'],
    suggest_status: 'Invoiced',
    label_prefix: 'Invoice created',
  },
  certificate_pdf_generated: {
    trigger_statuses: ['Invoiced'],
    suggest_status: 'Paid',
    label_prefix: 'Completion Certificate generated',
  },
};

/**
 * Show a confirmation toast offering to advance the appraisal one step in
 * the canonical STATUS_FLOW. Operator must click [Advance] — nothing
 * happens silently.
 *
 *   api               — axios instance from useAuth
 *   appraisalId       — the appraisal _id
 *   currentStatus     — the appraisal's current appraisal_status (NOT next)
 *   eventKey          — one of STAGE_BUMP_EVENTS keys
 *   onAdvanced(res)   — optional callback after a successful bump, given
 *                       the backend response so the caller can refresh UI
 */
export function promptStageBump({ api, appraisalId, currentStatus, eventKey, onAdvanced }) {
  const cfg = STAGE_BUMP_EVENTS[eventKey];
  if (!cfg) return false;
  if (!cfg.trigger_statuses.includes(currentStatus)) return false;

  toast(
    `${cfg.label_prefix} — bump status to "${cfg.suggest_status}"?`,
    {
      description: `Current: "${currentStatus}". One-click to keep your Kanban tidy.`,
      duration: 12000,
      action: {
        label: `Advance → ${cfg.suggest_status}`,
        onClick: async () => {
          try {
            const res = await api.post(`/pipeline/advance/${appraisalId}`);
            toast.success(`Status updated to "${res.data.new_status}"`);
            if (onAdvanced) onAdvanced(res.data);
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to advance status');
          }
        },
      },
      cancel: { label: 'Not yet', onClick: () => {} },
    }
  );
  return true;
}
